import React from 'react';
import { bearerHeaders } from '../api.ts';
import { resolveUrl } from '../api';
import { getTheme, useThemeMode } from '../theme';
import { VILTSLAG_LIST } from './constants';
import { mediaPublicUrl } from './pinUtils';
import type { MarkPin } from './types';

export function FalltViltModal({ lat, lng, markId, token, onSave, onCancel }: {
  lat: number; lng: number; markId: string; token: string;
  onSave: (pin: MarkPin) => void; onCancel: () => void;
}) {
  const [viltslag, setViltslag] = React.useState('Älg');
  const [kon, setKon] = React.useState('Okänt');
  const [alder, setAlder] = React.useState('Okänt');
  const [photoUrl, setPhotoUrl] = React.useState('');
  const [existingQid, setExistingQid] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [uploadingPhoto, setUploadingPhoto] = React.useState(false);
  const [err, setErr] = React.useState('');

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(resolveUrl('/api/upload'), { method: 'POST', body: formData });
      const data = await res.json();
      if (data.url) setPhotoUrl(data.url);
    } catch { setErr('Fotouppladdning misslyckades'); }
    finally { setUploadingPhoto(false); }
  };

  const handleSave = async () => {
    setSaving(true); setErr('');
    try {
      const res = await fetch(resolveUrl('/graphql'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Api-Key': 'dev-secret', ...bearerHeaders(token) },
        body: JSON.stringify({
          query: `mutation($input: CreateFalltViltPinInput!) { createFalltViltPin(input: $input) { id markId type lat lng name viltslag kon alder jagarEmail falltStatus qregistryObjectId photoUrls falltViltEvents { id eventType authorEmail oldStatus newStatus commentText newLat newLng photoUrl createdAt } createdAt } }`,
          variables: { input: { markId, lat, lng, viltslag, kon, alder, photoUrl: photoUrl || undefined, existingQregistryId: existingQid || undefined } },
        }),
      });
      const json = await res.json();
      if (json.errors) throw new Error(json.errors[0].message);
      onSave(json.data.createFalltViltPin);
    } catch (e: any) { setErr(e.message); }
    finally { setSaving(false); }
  };

  const { mode } = useThemeMode();
  const th = React.useMemo(() => getTheme(mode), [mode]);
  const T2 = React.useMemo(() => ({
    card: th.surface,
    field: th.bg,
    border: th.border,
    text: th.text,
    sub: th.textSub,
    amber: th.amber,
    overlay: th.overlay,
  }), [th]);
  const inp: React.CSSProperties = { width: '100%', background: T2.field, border: `1px solid ${T2.border}`, borderRadius: 4, padding: '8px 12px', color: T2.text, fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' };
  const lbl: React.CSSProperties = { display: 'block', color: T2.sub, fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 5 };

  return (
    <div style={{ position: 'fixed', inset: 0, background: T2.overlay, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10002 }}>
      <div style={{ background: T2.card, borderRadius: 8, padding: 24, minWidth: 320, maxWidth: 400, width: '90%', border: `1px solid ${T2.border}`, boxShadow: '0 12px 48px rgba(0,0,0,0.6)' }}>
        <div style={{ fontWeight: 700, color: '#E53935', fontSize: 18, marginBottom: 20 }}>Registrera fällt vilt</div>

        {/* Djurart */}
        <div style={{ marginBottom: 14 }}>
          <label style={lbl}>Djurart / Viltslag</label>
          <select value={viltslag} onChange={e => setViltslag(e.target.value)} style={inp}>
            {VILTSLAG_LIST.map(v => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>

        {/* Kön */}
        <div style={{ marginBottom: 14 }}>
          <label style={lbl}>Kön</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {['Hane','Hona','Okänt'].map(k => (
              <button key={k} onClick={() => setKon(k)} style={{
                flex: 1, padding: '7px 0', borderRadius: 4, border: `1px solid ${kon === k ? T2.amber : T2.border}`,
                background: kon === k ? T2.amber : 'transparent', color: kon === k ? '#0a0a0a' : T2.text,
                cursor: 'pointer', fontWeight: 600, fontSize: 13,
              }}>{k}</button>
            ))}
          </div>
        </div>

        {/* Ålder */}
        <div style={{ marginBottom: 14 }}>
          <label style={lbl}>Ålder</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {['Ungdjur','Vuxet','Okänt'].map(a => (
              <button key={a} onClick={() => setAlder(a)} style={{
                flex: 1, padding: '7px 0', borderRadius: 4, border: `1px solid ${alder === a ? T2.amber : T2.border}`,
                background: alder === a ? T2.amber : 'transparent', color: alder === a ? '#0a0a0a' : T2.text,
                cursor: 'pointer', fontWeight: 600, fontSize: 13,
              }}>{a}</button>
            ))}
          </div>
        </div>

        {/* Photo */}
        <div style={{ marginBottom: 14 }}>
          <label style={lbl}>Foto (valfritt)</label>
          {photoUrl ? (
            <div style={{ position: 'relative', marginBottom: 6 }}>
              <img src={mediaPublicUrl(photoUrl)} alt="Foto" style={{ width: '100%', height: 100, objectFit: 'cover', borderRadius: 4, border: `1px solid ${T2.border}` }} />
              <button onClick={() => setPhotoUrl('')} style={{ position: 'absolute', top: 4, right: 4, background: 'rgba(0,0,0,0.6)', border: 'none', color: 'white', borderRadius: '50%', width: 22, height: 22, cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
            </div>
          ) : (
            <label style={{ display: 'block', padding: '8px 12px', borderRadius: 4, border: `1px solid ${T2.border}`, color: T2.sub, cursor: 'pointer', fontSize: 13, textAlign: 'center', opacity: uploadingPhoto ? 0.6 : 1 }}>
              {uploadingPhoto ? 'Laddar upp...' : '+ Lägg till foto'}
              <input type="file" accept="image/*" capture="environment" onChange={handlePhotoUpload} style={{ display: 'none' }} disabled={uploadingPhoto} />
            </label>
          )}
        </div>

        {/* Qregistry ID */}
        <div style={{ marginBottom: 18 }}>
          <label style={lbl}>Länka befintligt Qregistry-ID (valfritt)</label>
          <input value={existingQid} onChange={e => setExistingQid(e.target.value)} placeholder="Qregistry object ID" style={inp} />
        </div>

        {err && <div style={{ color: '#ff6b6b', fontSize: 13, marginBottom: 12 }}>{err}</div>}

        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={handleSave} disabled={saving} style={{ flex: 2, padding: '10px 0', borderRadius: 4, border: 'none', background: '#E53935', color: 'white', fontWeight: 700, cursor: 'pointer', fontSize: 14, opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Sparar...' : 'Registrera'}
          </button>
          <button onClick={onCancel} style={{ flex: 1, padding: '10px 0', borderRadius: 4, border: `1px solid ${T2.border}`, background: 'transparent', color: T2.sub, cursor: 'pointer', fontSize: 14 }}>
            Avbryt
          </button>
        </div>
      </div>
    </div>
  );
}
